'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface ZonaFisica {
  id: string;
  nome: string;
  tipo: string;
}

interface UpsDispositivo {
  id: string;
  nome: string;
  modello: string;
  produttore: string;
  stato: string;
  zona_id: string;
  potenza_va: number;
  potenza_w: number;
  carico_percentuale: number;
  autonomia_minuti: number;
  batteria_percentuale: number;
  ultima_verifica: string;
  prossimo_test: string;
  ip_address: string;
  note: string;
  zona?: ZonaFisica;
}

interface AlimentazioneReparto {
  id: string;
  reparto_id: string;
  zona_id: string;
  stato: string;
  tensione_v: number;
  corrente_a: number;
  potenza_kw: number;
  ups_id: string;
  ultimo_aggiornamento: string;
  zona?: ZonaFisica;
  ups?: UpsDispositivo;
}

interface AlertAmbientale {
  id: string;
  titolo: string;
  descrizione: string;
  livello: string;
  stato: string;
  ups_id: string | null;
  created_at: string;
}

const UPS_STATO_BG: Record<string, string> = {
  normale: 'bg-emerald-500/20 text-emerald-400',
  batteria: 'bg-amber-500/20 text-amber-400',
  bypass: 'bg-blue-500/20 text-blue-400',
  guasto: 'bg-red-500/20 text-red-400',
  offline: 'bg-gray-500/20 text-gray-400',
  test: 'bg-purple-500/20 text-purple-400',
};

const ALIM_STATO_BG: Record<string, string> = {
  normale: 'bg-emerald-500/20 text-emerald-400',
  interruzione: 'bg-red-500/20 text-red-400',
  sbalzo: 'bg-amber-500/20 text-amber-400',
  degradata: 'bg-orange-500/20 text-orange-400',
  manutenzione: 'bg-blue-500/20 text-blue-400',
};

const LIVELLO_BG: Record<string, string> = {
  info: 'bg-sky-500/20 text-sky-400',
  warning: 'bg-amber-500/20 text-amber-400',
  critico: 'bg-red-500/20 text-red-400',
  emergenza: 'bg-red-700/30 text-red-300',
};

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function UpsAlimentazioneContent() {
  const [upsList, setUpsList] = useState<UpsDispositivo[]>([]);
  const [alimentazioni, setAlimentazioni] = useState<AlimentazioneReparto[]>([]);
  const [alerts, setAlerts] = useState<AlertAmbientale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ups' | 'alimentazione' | 'alerts'>('ups');
  const [showAddUps, setShowAddUps] = useState(false);
  const [editUps, setEditUps] = useState<UpsDispositivo | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [zone, setZone] = useState<ZonaFisica[]>([]);
  const [formUps, setFormUps] = useState({ nome: '', modello: '', produttore: '', zona_id: '', potenza_va: 0, potenza_w: 0, ip_address: '', note: '' });

  const fetchData = useCallback(async () => {
    try {
      const [upsRes, alimRes, alertsRes, zoneRes] = await Promise.all([
        supabase.from('ups_dispositivi').select('*, zona:zone_fisiche(id,nome,tipo)').order('nome'),
        supabase.from('alimentazione_reparti').select('*, zona:zone_fisiche(id,nome,tipo), ups:ups_dispositivi(id,nome,stato)').order('ultimo_aggiornamento', { ascending: false }),
        supabase.from('alert_ambientali').select('*').in('stato', ['attivo', 'riconosciuto']).order('created_at', { ascending: false }).limit(20),
        supabase.from('zone_fisiche').select('id,nome,tipo').order('nome'),
      ]);
      if (upsRes.error) throw upsRes.error;
      if (alimRes.error) throw alimRes.error;
      if (alertsRes.error) throw alertsRes.error;
      if (zoneRes.error) throw zoneRes.error;
      setUpsList(upsRes.data || []);
      setAlimentazioni(alimRes.data || []);
      setAlerts(alertsRes.data || []);
      setZone(zoneRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const riconosciAlert = async (alertId: string) => {
    await supabase.from('alert_ambientali').update({ stato: 'riconosciuto', riconosciuto_at: new Date().toISOString() }).eq('id', alertId);
    fetchData();
  };

  const risolviAlert = async (alertId: string) => {
    await supabase.from('alert_ambientali').update({ stato: 'risolto', risolto_at: new Date().toISOString() }).eq('id', alertId);
    fetchData();
  };

  const upsInBatteria = upsList.filter(u => u.stato === 'batteria').length;
  const upsGuasti = upsList.filter(u => u.stato === 'guasto').length;
  const alimentazioneInterrotta = alimentazioni.filter(a => a.stato === 'interruzione').length;
  const alertAttivi = alerts.filter(a => a.stato === 'attivo').length;

  const openAddUps = () => {
    setEditUps(null);
    setFormUps({ nome: '', modello: '', produttore: '', zona_id: '', potenza_va: 0, potenza_w: 0, ip_address: '', note: '' });
    setShowAddUps(true);
  };

  const openEditUps = (ups: UpsDispositivo) => {
    setEditUps(ups);
    setFormUps({ nome: ups.nome, modello: ups.modello || '', produttore: ups.produttore || '', zona_id: ups.zona_id || '', potenza_va: ups.potenza_va || 0, potenza_w: ups.potenza_w || 0, ip_address: ups.ip_address || '', note: ups.note || '' });
    setShowAddUps(true);
  };

  const handleSaveUps = async () => {
    if (!formUps.nome.trim()) { setError('Nome UPS obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...formUps, zona_id: formUps.zona_id || null, stato: 'normale', batteria_percentuale: 100, carico_percentuale: 0, autonomia_minuti: 0 };
      if (editUps) {
        const { error: err } = await supabase.from('ups_dispositivi').update({ nome: formUps.nome, modello: formUps.modello, produttore: formUps.produttore, zona_id: formUps.zona_id || null, potenza_va: formUps.potenza_va, potenza_w: formUps.potenza_w, ip_address: formUps.ip_address, note: formUps.note }).eq('id', editUps.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('ups_dispositivi').insert(payload);
        if (err) throw err;
      }
      setShowAddUps(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUps = async (id: string) => {
    try {
      const { error: err } = await supabase.from('ups_dispositivi').delete().eq('id', id);
      if (err) throw err;
      setDeleteConfirmId(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">UPS e Alimentazione</h1>
            <p className="text-muted-color text-sm mt-1">Stato alimentazione elettrica, UPS e alert interruzioni</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={openAddUps} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Aggiungi UPS
            </button>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>}

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">UPS Totali</p>
            <p className="text-2xl font-bold text-white">{upsList.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">In Batteria</p>
            <p className={`text-2xl font-bold ${upsInBatteria > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{upsInBatteria}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Guasti</p>
            <p className={`text-2xl font-bold ${upsGuasti > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{upsGuasti}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Interruzioni Rete</p>
            <p className={`text-2xl font-bold ${alimentazioneInterrotta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{alimentazioneInterrotta}</p>
          </div>
        </div>

        {/* Alert Banner */}
        {alertAttivi > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="BoltIcon" size={18} className="text-red-400" />
              <span className="text-red-400 font-semibold text-sm">Alert Alimentazione Attivi ({alertAttivi})</span>
            </div>
            {alerts.filter(a => a.stato === 'attivo').map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-surface rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${LIVELLO_BG[alert.livello] || 'bg-gray-500/20 text-gray-400'}`}>
                    {alert.livello.toUpperCase()}
                  </span>
                  <div>
                    <p className="text-white text-sm font-semibold">{alert.titolo}</p>
                    <p className="text-muted-color text-xs">{alert.descrizione}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => riconosciAlert(alert.id)} className="text-xs px-3 py-1 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">Riconosci</button>
                  <button onClick={() => risolviAlert(alert.id)} className="text-xs px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors">Risolvi</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-surface rounded-lg p-1 w-fit border border-border">
          {(['ups', 'alimentazione', 'alerts'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${activeTab === tab ? 'bg-sky-500 text-white' : 'text-muted-color hover:text-white'}`}>
              {tab === 'ups' ? 'UPS' : tab === 'alimentazione' ? 'Alimentazione Reparti' : `Alert (${alertAttivi})`}
            </button>
          ))}
        </div>

        {/* UPS List */}
        {activeTab === 'ups' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upsList.map(ups => (
              <div key={ups.id} className={`bg-surface rounded-xl border p-5 ${ups.stato === 'batteria' ? 'border-amber-500/40' : ups.stato === 'guasto' ? 'border-red-500/40' : 'border-border'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-white font-semibold">{ups.nome}</p>
                    <p className="text-muted-color text-xs">{ups.produttore} {ups.modello}</p>
                    <p className="text-muted-color text-xs">{ups.zona?.nome || '—'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${UPS_STATO_BG[ups.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                      {ups.stato.toUpperCase()}
                    </span>
                    <button onClick={() => openEditUps(ups)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                      <Icon name="PencilIcon" size={14} />
                    </button>
                    <button onClick={() => setDeleteConfirmId(ups.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                      <Icon name="TrashIcon" size={14} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-surface-elevated rounded-lg p-3 text-center">
                    <p className="text-muted-color text-xs mb-1">Batteria</p>
                    <p className={`text-lg font-bold ${ups.batteria_percentuale < 30 ? 'text-red-400' : ups.batteria_percentuale < 60 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {ups.batteria_percentuale?.toFixed(0)}%
                    </p>
                  </div>
                  <div className="bg-surface-elevated rounded-lg p-3 text-center">
                    <p className="text-muted-color text-xs mb-1">Carico</p>
                    <p className="text-lg font-bold text-sky-400">{ups.carico_percentuale?.toFixed(0)}%</p>
                  </div>
                  <div className="bg-surface-elevated rounded-lg p-3 text-center">
                    <p className="text-muted-color text-xs mb-1">Autonomia</p>
                    <p className="text-lg font-bold text-white">{ups.autonomia_minuti}m</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-color">
                  <span>Potenza: {ups.potenza_va} VA / {ups.potenza_w} W</span>
                  {ups.ip_address && <span className="font-mono">{ups.ip_address}</span>}
                </div>
                {ups.prossimo_test && (
                  <p className="text-xs text-muted-color mt-1">
                    Prossimo test: {new Date(ups.prossimo_test).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
            ))}
            {upsList.length === 0 && (
              <div className="col-span-2 text-center py-12 text-muted-color">Nessun UPS trovato</div>
            )}
          </div>
        )}

        {/* Alimentazione Reparti */}
        {activeTab === 'alimentazione' && (
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">ZONA</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">STATO</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">TENSIONE</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">CORRENTE</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">POTENZA</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">UPS</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">AGGIORNAMENTO</th>
                </tr>
              </thead>
              <tbody>
                {alimentazioni.map(a => (
                  <tr key={a.id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                    <td className="px-4 py-3 text-white text-sm font-semibold">{(a as any).zona?.nome || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ALIM_STATO_BG[a.stato] || 'bg-gray-500/20 text-gray-400'}`}>
                        {a.stato.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-secondary-color text-sm">{a.tensione_v?.toFixed(1)} V</td>
                    <td className="px-4 py-3 text-secondary-color text-sm">{a.corrente_a?.toFixed(1)} A</td>
                    <td className="px-4 py-3 text-secondary-color text-sm">{a.potenza_kw?.toFixed(2)} kW</td>
                    <td className="px-4 py-3 text-secondary-color text-sm">{(a as any).ups?.nome || '—'}</td>
                    <td className="px-4 py-3 text-muted-color text-xs">{new Date(a.ultimo_aggiornamento).toLocaleString('it-IT')}</td>
                  </tr>
                ))}
                {alimentazioni.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-color text-sm">Nessun dato alimentazione</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tutti gli Alert */}
        {activeTab === 'alerts' && (
          <div className="space-y-3">
            {alerts.map(alert => (
              <div key={alert.id} className="bg-surface rounded-xl border border-border p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 ${LIVELLO_BG[alert.livello] || 'bg-gray-500/20 text-gray-400'}`}>
                      {alert.livello.toUpperCase()}
                    </span>
                    <div>
                      <p className="text-white font-semibold text-sm">{alert.titolo}</p>
                      <p className="text-muted-color text-xs mt-1">{alert.descrizione}</p>
                      <p className="text-muted-color text-xs mt-1">{new Date(alert.created_at).toLocaleString('it-IT')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${alert.stato === 'attivo' ? 'bg-red-500/20 text-red-400' : alert.stato === 'riconosciuto' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                      {alert.stato}
                    </span>
                    {alert.stato === 'attivo' && (
                      <button onClick={() => riconosciAlert(alert.id)} className="text-xs px-3 py-1 bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">Riconosci</button>
                    )}
                    {alert.stato !== 'risolto' && (
                      <button onClick={() => risolviAlert(alert.id)} className="text-xs px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors">Risolvi</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {alerts.length === 0 && (
              <div className="text-center py-12 text-muted-color">Nessun alert trovato</div>
            )}
          </div>
        )}

        {/* Delete Confirm */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Elimina UPS</h3>
              <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare questo UPS?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
                <button onClick={() => handleDeleteUps(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all">Elimina</button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit UPS Modal */}
        {showAddUps && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-white">{editUps ? 'Modifica UPS' : 'Aggiungi UPS'}</h3>
                <button onClick={() => setShowAddUps(false)} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Nome *</label>
                    <input value={formUps.nome} onChange={e => setFormUps(p => ({ ...p, nome: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Produttore</label>
                    <input value={formUps.produttore} onChange={e => setFormUps(p => ({ ...p, produttore: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Modello</label>
                    <input value={formUps.modello} onChange={e => setFormUps(p => ({ ...p, modello: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={formUps.zona_id} onChange={e => setFormUps(p => ({ ...p, zona_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Potenza (VA)</label>
                    <input type="number" min="0" value={formUps.potenza_va} onChange={e => setFormUps(p => ({ ...p, potenza_va: parseInt(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Potenza (W)</label>
                    <input type="number" min="0" value={formUps.potenza_w} onChange={e => setFormUps(p => ({ ...p, potenza_w: parseInt(e.target.value) || 0 }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">IP Address</label>
                  <input value={formUps.ip_address} onChange={e => setFormUps(p => ({ ...p, ip_address: e.target.value }))} placeholder="es. 192.168.1.100" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Note</label>
                  <textarea value={formUps.note} onChange={e => setFormUps(p => ({ ...p, note: e.target.value }))} rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500 resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowAddUps(false)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={handleSaveUps} disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                  {saving ? 'Salvataggio...' : editUps ? 'Aggiorna' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
